import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Token storage keys (same as AuthContext for compatibility)
const TOKEN_KEY = 'la_tk';
const REFRESH_KEY = 'la_rt';

// Decode token from storage (reverse of AuthContext's encodeToken)
const decodeToken = (encoded) => {
  if (!encoded) return null;
  try {
    return decodeURIComponent(atob(encoded).split('').reverse().join(''));
  } catch {
    // If decoding fails, assume it's already plain (migration support)
    return encoded;
  }
};

// Get token from secure storage
const getStoredToken = (isRefresh = false) => {
  try {
    const key = isRefresh ? REFRESH_KEY : TOKEN_KEY;
    const encoded = localStorage.getItem(key);
    return decodeToken(encoded);
  } catch {
    return null;
  }
};

// Store token securely (same encoding as AuthContext)
const storeToken = (token, isRefresh = false) => {
  try {
    const key = isRefresh ? REFRESH_KEY : TOKEN_KEY;
    if (token) {
      const encoded = btoa(encodeURIComponent(token).split('').reverse().join(''));
      localStorage.setItem(key, encoded);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Fallback to plain storage
    const key = isRefresh ? REFRESH_KEY : TOKEN_KEY;
    if (token) {
      localStorage.setItem(key, token);
    } else {
      localStorage.removeItem(key);
    }
  }
};

// Clear all tokens
const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem('la_exp');
  localStorage.removeItem('la_int');
};

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 180000, // 3 minutes default timeout (for video uploads)
});

// Create a special instance for large file uploads
export const uploadApi = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 300000, // 5 minutes for file uploads
  maxContentLength: 100 * 1024 * 1024, // 100MB max
  maxBodyLength: 100 * 1024 * 1024,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getStoredToken(false);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Same interceptor for upload API
uploadApi.interceptors.request.use(
  (config) => {
    const token = getStoredToken(false);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = getStoredToken(true);
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token, refresh_token: newRefreshToken } = response.data;
          storeToken(access_token, false);
          if (newRefreshToken) {
            storeToken(newRefreshToken, true);
          }
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
